import React, { useEffect, useMemo } from 'react';
import { Button, Card, Modal, Form, Input, Upload, Image, Space, Typography, Popconfirm, message, Row, Col, Spin, Tabs, Empty, DatePicker } from 'antd';
import { useNavigate } from 'react-router-dom';
import { PlusOutlined, UploadOutlined } from '@ant-design/icons';
import AdminNav from './AdminNav';
import moment from 'moment';
import {
  fetchBlogs as fetchBlogsApi,
  createBlog,
  updateBlog,
  deleteBlog,
  uploadBlogImage,
  deleteBlogImage,
  fetchBlog
} from './api';

const AdminBlogs = (props) => {
  const [blogs, setBlogs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [openModal, setOpenModal] = React.useState(false);
  const [openScheduledModal, setOpenScheduledModal] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [scheduledSaving, setScheduledSaving] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [scheduledEditing, setScheduledEditing] = React.useState(null);
  const [form] = Form.useForm();
  const [scheduledForm] = Form.useForm();
  const [imageFile, setImageFile] = React.useState(null);
  const [imagePreview, setImagePreview] = React.useState(null);
  const [activeTab, setActiveTab] = React.useState('published');
  const [scheduledImageFile, setScheduledImageFile] = React.useState(null);
  const [scheduledImagePreview, setScheduledImagePreview] = React.useState(null);
  const navigate = useNavigate();

  const [nowTick, setNowTick] = React.useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const list = await fetchBlogsApi();
      setBlogs(list.map((b, idx) => ({ key: b.id || idx, ...b })));
    } catch (e) {
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  };

  const onEditScheduled = (record) => {
    setScheduledEditing(record);
    setScheduledImageFile(null);
    setScheduledImagePreview(null);
    scheduledForm.resetFields();
    scheduledForm.setFieldsValue({
      heading: record.heading,
      description: record.description,
      slug: record.slug,
      scheduledAt: record.scheduledAt ? moment(new Date(typeof record.scheduledAt === 'number' ? record.scheduledAt : Date.parse(record.scheduledAt))) : null,
    });
    setOpenScheduledModal(true);
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const columns = useMemo(
    () => [],
    []
  );

  const generateSlug = (text) => {
    return text
      .toString()
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^\w\-]+/g, '')
      .replace(/\-\-+/g, '-')
      .replace(/^-+/, '')
      .replace(/-+$/, '');
  };

  const onHeadingChange = (e) => {
    const heading = e.target.value;
    const slug = generateSlug(heading);
    form.setFieldsValue({ slug });
  };

  const onCreate = () => {
    setEditing(null);
    setImageFile(null);
    setImagePreview(null);
    form.resetFields();
    setOpenModal(true);
  };

  const onCreateScheduled = () => {
    setScheduledEditing(null);
    setScheduledImageFile(null);
    setScheduledImagePreview(null);
    scheduledForm.resetFields();
    setOpenScheduledModal(true);
  };

  const onEdit = async (record) => {
    setEditing(record);
    setImageFile(null);
    setImagePreview(null);
    form.setFieldsValue({ heading: record.heading, description: record.description, slug: record.slug });
    setOpenModal(true);
    try {
      const idOrSlug = record.id || record.slug || generateSlug(record.heading);
      const detail = await fetchBlog(idOrSlug);
      if (detail) {
        setEditing((prev) => ({ ...prev, ...detail }));
        form.setFieldsValue({
          heading: detail.heading ?? record.heading,
          description: detail.description ?? record.description,
          slug: detail.slug ?? record.slug
        });
      }
    } catch (e) {
      // ignore
    }
  };

  const onDelete = async (record) => {
    try {
      const ok = await deleteBlog({ id: record.id });
      if (!ok) throw new Error('Delete failed');
      message.success('Blog deleted');
      fetchBlogs();
    } catch (e) {
      message.error('Delete failed');
    }
  };

  const uploadImageIfNeeded = async () => {
    if (!imageFile) return { url: editing?.imageUrl || '', key: editing?.imageKey || '' };
    const { url, key } = await uploadBlogImage(imageFile);
    // const value = await uploadBlogImage(imageFile);
    console.log(key, "key")
    return { url, key };
  };

  const onSubmit = async () => {
    if (saving) return;
    setSaving(true);
    const values = await form.validateFields();
    console.log(editing, "value")
    if (editing && imageFile && editing.imageKey) {
      await deleteBlogImage(editing.imageKey);
    }
    const { url, key } = await uploadImageIfNeeded();
    const payload = { heading: values.heading, description: values.description, imageUrl: url, imageKey: key, slug: values.slug };
    try {
      if (editing) {
        const ok = await updateBlog({ id: editing.id, ...payload });
        if (!ok) throw new Error('Update failed');
        message.success('Blog updated');
      } else {
        const ok = await createBlog(payload);
        if (!ok) throw new Error('Create failed');
        message.success('Blog created');
      }
      setOpenModal(false);
      fetchBlogs();
    } catch (e) {
      message.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const uploadScheduledImageIfNeeded = async () => {
    if (!scheduledImageFile) {
      return {
        url: scheduledEditing?.imageUrl || '',
        key: scheduledEditing?.imageKey || ''
      };
    }
    if (scheduledEditing?.imageKey) {
      try {
        await deleteBlogImage(scheduledEditing.imageKey);
      } catch (e) {
        // ignore
      }
    }
    const { url, key } = await uploadBlogImage(scheduledImageFile);
    return { url, key };
  };

  const parseTs = (t) => {
    if (typeof t === 'number' && isFinite(t)) return t;
    const p = Date.parse(t);
    return Number.isFinite(p) ? p : 0;
  };
  const isLive = (t) => {
    const ts = parseTs(t);
    return ts > 0 && ts <= nowTick;
  };

  const scheduledBlogs = React.useMemo(() => {
    const future = blogs.filter((b) => {
      if (!b || b.status !== 'scheduled') return false;
      const t = typeof b.scheduledAt === 'number' ? b.scheduledAt : Date.parse(b.scheduledAt);
      return Number.isFinite(t) && t > nowTick;
    });
    future.sort((a, b) => parseTs(a.scheduledAt) - parseTs(b.scheduledAt));
    return future;
  }, [blogs, nowTick]);

  const publishedBlogs = React.useMemo(() => {
    return blogs.filter((b) => {
      if (!b || b.hidden) return false;
      if (!b.status || b.status === 'published') return true;
      if (b.status === 'scheduled') {
        const t = typeof b.scheduledAt === 'number' ? b.scheduledAt : Date.parse(b.scheduledAt);
        return Number.isFinite(t) && t <= nowTick;
      }
      return false;
    });
  }, [blogs, nowTick]);

  const onSubmitScheduled = async () => {
    if (scheduledSaving) return;
    const values = await scheduledForm.validateFields();
    const editingIsLive = scheduledEditing && isLive(scheduledEditing.scheduledAt);
    const when = values.scheduledAt ? values.scheduledAt.valueOf() : (scheduledEditing?.scheduledAt ? parseTs(scheduledEditing.scheduledAt) : null);
    if (!editingIsLive) {
      if (!when || when <= Date.now()) {
        message.error('Select a future date and time');
        return;
      }
    }
    try {
      setScheduledSaving(true);
      const { url, key } = await uploadScheduledImageIfNeeded();
      const payload = {
        heading: values.heading,
        description: values.description,
        imageUrl: url || '',
        imageKey: key || '',
        slug: values.slug,
        scheduledAt: when,
        status: 'scheduled'
      };
      if (scheduledEditing) {
        const updatePayload = editingIsLive ? { ...payload, scheduledAt: undefined } : payload;
        const ok = await updateBlog({ id: scheduledEditing.id, ...updatePayload });
        if (!ok) throw new Error('Update scheduled failed');
        message.success('Scheduled blog updated');
      } else {
        const created = await createBlog(payload);
        const gotId = created && (created.id || (created.data && created.data.id));
        if (!gotId) throw new Error('Create scheduled failed');
        message.success('Scheduled blog created');
      }
      setOpenScheduledModal(false);
      fetchBlogs();
    } catch (e) {
      message.error('Failed to schedule blog');
    } finally {
      setScheduledSaving(false);
    }
  };

  const onCancelSchedule = async (record) => {
    try {
      const ok = await deleteBlog({ id: record.id });
      if (!ok) throw new Error('Delete failed');
      message.success('Scheduled blog deleted');
      fetchBlogs();
    } catch (e) {
      message.error('Delete failed');
    }
  };

  const formatDateTime = (ts) => {
    try {
      const d = typeof ts === 'number' ? new Date(ts) : new Date(Date.parse(ts));
      return isNaN(d.getTime()) ? '' : d.toLocaleString();
    } catch (e) {
      return String(ts);
    }
  };

  const Countdown = ({ target }) => {
    const [now, setNow] = React.useState(Date.now());
    useEffect(() => {
      const id = setInterval(() => setNow(Date.now()), 1000);
      return () => clearInterval(id);
    }, []);
    const normalize = (t) => {
      if (typeof t === 'number' && !Number.isNaN(t) && isFinite(t)) return t;
      const parsed = Date.parse(t);
      return Number.isFinite(parsed) ? parsed : 0;
    };
    const tgt = normalize(target);
    const remaining = Math.max(0, tgt - now);
    const totalSeconds = Math.floor(remaining / 1000);
    const days = Math.floor(totalSeconds / (24 * 3600));
    const hours = Math.floor((totalSeconds % (24 * 3600)) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const pad = (n) => String(n).padStart(2, '0');
    const text = days > 0
      ? `${days}d ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
      : `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    return <span>{text}</span>;
  };

  return (
    <div>
      <AdminNav {...props}/>
      <div style={{ padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <Typography.Title level={3} style={{ margin: 0 }}>Blogs</Typography.Title>
          {activeTab === 'published' && (
            <div style={{ marginLeft: 'auto' }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={onCreate}>New Blog</Button>
            </div>
          )}
          {activeTab === 'scheduled' && (
            <div style={{ marginLeft: 'auto' }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={onCreateScheduled}>New Blog</Button>
            </div>
          )}
        </div>
        <Tabs activeKey={activeTab} onChange={setActiveTab} style={{ marginBottom: 12 }}>
          <Tabs.TabPane tab="Published" key="published">
            <Input.Search
              placeholder="Search articles or keywords"
              allowClear
              onChange={() => setBlogs((prev) => prev.map((b) => ({ ...b, hidden: false })))}
              style={{ maxWidth: 600, marginBottom: 16 }}
            />
            {loading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240 }}>
                <Spin size="large" />
              </div>
            ) : (
              <Row gutter={[16, 16]}>
                {publishedBlogs.map((blog) => (
                  <Col key={blog.key} xs={24} sm={12} md={8}>
                    <Card
                      hoverable
                      cover={blog.imageUrl ? <img alt={blog.heading} src={blog.imageUrl} style={{ height: 180, objectFit: 'cover' }} /> : null}
                      onClick={() => navigate(`/admin/blog/${blog.slug || generateSlug(blog.heading) || blog.id || blog.key}`)}
                    >
                      <Typography.Text type="secondary">{formatDateTime(blog.date)}</Typography.Text>
                      <Typography.Title level={5} style={{ marginTop: 8 }}>{blog.heading}</Typography.Title>
                      <Typography.Paragraph ellipsis={{ rows: 2 }}>{blog.description}</Typography.Paragraph>
                      <Space>
                        <Button type="link" onClick={(e) => { e.stopPropagation(); onEdit(blog); }}>Edit</Button>
                        <Popconfirm
                          title="Delete this blog?"
                          onConfirm={(e) => { e?.stopPropagation?.(); onDelete(blog); }}
                          onCancel={(e) => { e?.stopPropagation?.(); }}
                        >
                          <Button danger type="link" onClick={(e) => e.stopPropagation()}>Delete</Button>
                        </Popconfirm>
                      </Space>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </Tabs.TabPane>
          <Tabs.TabPane tab="Scheduled" key="scheduled">
            {loading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240 }}>
                <Spin size="large" />
              </div>
            ) : scheduledBlogs.length > 0 ? (
              <Row gutter={[16, 16]}>
                {scheduledBlogs.map((blog) => (
                  <Col key={blog.key || blog.id} xs={24} sm={12} md={8}>
                    <Card
                      hoverable
                      cover={
                        (blog.imageUrl) ? (() => {
                          const live = (() => {
                            const t = typeof blog.scheduledAt === 'number' ? blog.scheduledAt : Date.parse(blog.scheduledAt);
                            return Number.isFinite(t) && t <= nowTick;
                          })();
                          return (
                          <div style={{ position: 'relative', height: 180, overflow: 'hidden' }}>
                            <img
                              alt={blog.heading}
                              src={blog.imageUrl}
                              style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: live ? 1 : 0.4 }}
                            />
                            {!live && (
                              <div
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                                fontWeight: 600,
                                fontSize: 18,
                                textShadow: '0 1px 2px rgba(0,0,0,0.6)',
                              }}
                            >
                              <div style={{ background: 'rgba(0,0,0,0.4)', padding: '6px 12px', borderRadius: 6 }}>
                                <span style={{ marginRight: 8 }}>Goes live in</span>
                                <Countdown target={blog.scheduledAt} />
                              </div>
                            </div>
                            )}
                          </div>
                          );
                        })() : null
                      }
                      onClick={() => navigate(`/admin/blog/${blog.slug || generateSlug(blog.heading) || blog.id || blog.key}`)}
                    >
                      <Typography.Text type="secondary">{formatDateTime(blog.scheduledAt)}</Typography.Text>
                      <Typography.Title level={5} style={{ marginTop: 8 }}>{blog.heading}</Typography.Title>
                      <Typography.Paragraph ellipsis={{ rows: 2 }}>{blog.description}</Typography.Paragraph>
                      <Space>
                        <Button type="link" onClick={(e) => { e.stopPropagation(); onEditScheduled(blog); }}>Edit</Button>
                        <Popconfirm
                          title={(() => {
                            const live = isLive(blog.scheduledAt);
                            return live ? "Delete this blog?" : "Cancel this schedule?";
                          })()}
                          onConfirm={(e) => { e?.stopPropagation?.(); onCancelSchedule(blog); }}
                          onCancel={(e) => { e?.stopPropagation?.(); }}
                        >
                          <Button danger type="link" onClick={(e) => e.stopPropagation()}>
                            {isLive(blog.scheduledAt) ? 'Delete' : 'Cancel'}
                          </Button>
                        </Popconfirm>
                      </Space>
                    </Card>
                  </Col>
                ))}
              </Row>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 240 }}>
                <Empty description="No scheduled blogs" />
                <Button type="primary" style={{ marginTop: 12 }} onClick={onCreateScheduled}>New Blog</Button>
              </div>
            )}
          </Tabs.TabPane>
        </Tabs>
        <Modal
          open={openModal}
          onCancel={() => setOpenModal(false)}
          onOk={onSubmit}
          okText={editing ? 'Update' : 'Create'}
          title={editing ? 'Edit Blog' : 'Create Blog'}
          confirmLoading={saving}
          destroyOnClose
        >
          <Form 
            form={form} 
            layout="vertical"
            onValuesChange={(changedValues) => {
              if (changedValues.heading) {
                const slug = generateSlug(changedValues.heading);
                form.setFieldsValue({ slug });
              }
            }}
          >
            <Form.Item name="heading" label="Heading" rules={[{ required: true }]}> 
              <Input placeholder="Enter blog heading" />
            </Form.Item>
            <Form.Item name="slug" label="Slug" rules={[{ required: true }]}> 
              <Input placeholder="Enter url slug" />
            </Form.Item>
            <Form.Item name="description" label="Description" rules={[{ required: true }]}> 
              <Input.TextArea rows={6} placeholder="Enter blog description" />
            </Form.Item>
            <Form.Item label="Image">
              <Upload
                accept="image/*"
                beforeUpload={(file) => { setImageFile(file); setImagePreview(URL.createObjectURL(file)); return false; }}
                maxCount={1}
              >
                <Button icon={<UploadOutlined />}>Select Image</Button>
              </Upload>
              {imagePreview && <Image src={imagePreview} width={160} style={{ marginTop: 12 }} />}
              {!imagePreview && editing?.imageUrl && <Image src={editing.imageUrl} width={160} style={{ marginTop: 12 }} />}
            </Form.Item>
          </Form>
        </Modal>
        <Modal
          open={openScheduledModal}
          onCancel={() => setOpenScheduledModal(false)}
          onOk={onSubmitScheduled}
          okText={scheduledEditing ? 'Update' : 'Create'}
          title={scheduledEditing ? 'Edit Scheduled Blog' : 'Schedule Blog'}
          confirmLoading={scheduledSaving}
          destroyOnClose
        >
          <Form 
            form={scheduledForm} 
            layout="vertical"
            onValuesChange={(changedValues) => {
              if (changedValues.heading) {
                const slug = generateSlug(changedValues.heading);
                scheduledForm.setFieldsValue({ slug });
              }
            }}
          >
            <Form.Item name="heading" label="Heading" rules={[{ required: true }]}> 
              <Input placeholder="Enter blog heading" />
            </Form.Item>
            <Form.Item name="slug" label="Slug" rules={[{ required: true }]}> 
              <Input placeholder="Enter url slug" />
            </Form.Item>
            <Form.Item name="description" label="Description" rules={[{ required: true }]}> 
              <Input.TextArea rows={6} placeholder="Enter blog description" />
            </Form.Item>
            <Form.Item
              name="scheduledAt"
              label="Schedule"
              rules={[{ required: true, message: 'Select date and time' }]}
            >
              <DatePicker
                showTime
                style={{ width: '100%' }}
                disabled={scheduledEditing ? isLive(scheduledEditing.scheduledAt) : false}
                inputReadOnly={scheduledEditing ? isLive(scheduledEditing.scheduledAt) : false}
                disabledDate={(current) => current && current.valueOf() < Date.now() - 24 * 60 * 60 * 1000}
              />
            </Form.Item>
            <Form.Item label="Image">
              <Upload
                accept="image/*"
                beforeUpload={(file) => { setScheduledImageFile(file); setScheduledImagePreview(URL.createObjectURL(file)); return false; }}
                maxCount={1}
              >
                <Button icon={<UploadOutlined />}>Select Image</Button>
              </Upload>
              {scheduledImagePreview && <Image src={scheduledImagePreview} width={160} style={{ marginTop: 12 }} />}
              {!scheduledImagePreview && scheduledEditing?.imageUrl && <Image src={scheduledEditing.imageUrl} width={160} style={{ marginTop: 12 }} />}
            </Form.Item>
          </Form>
        </Modal>
  </div>
  </div>
  );
};

export default AdminBlogs;
